import { Box, NavLink, Stack, Text, TextInput } from '@mantine/core';
import { i18nKeys, useTranslation } from 'locales';
import { Icon, IconName } from 'shared/components/Icon';
import { filterToText } from 'shared/serializer';
import type { TableFilter } from 'types/views';

import {
  HANDLER_SUFFIX,
  MAX_PREFIX,
  MIN_PREFIX,
  OPERATOR_SUFFIX,
  RANGE_END_SUFFIX,
  RANGE_START_SUFFIX,
} from '../filters/filterGrammar';
import { isFilterActive, labelFilterValue } from '../filters/filterValueLabel';
import type { useTableFilterState } from '../hooks/useTableFilterState';

type FilterState = ReturnType<typeof useTableFilterState>;

type Props = {
  searchText: string;
  setSearchInput: FilterState['setSearchInput'];
  visibleFilterCategories: FilterState['visibleFilterCategories'];
  filtersSearched: FilterState['filtersSearched'];
  filters: FilterState['filters'];
  filtersName: FilterState['filtersName'];
  constants: FilterState['constants'];
  company: FilterState['company'];
  showFilter: FilterState['showFilter'];
};

// The left-hand pane: the search box, the category headers, and the selectable
// filter rows. Filters with a value applied float to the top regardless of
// category (active-first); the rest sit under their category header, categories
// ordered by visibleFilterCategories. This replaces a flex-`order` layout whose
// positional rules misfired whenever the drawer's structure changed.
export function FilterList({
  searchText,
  setSearchInput,
  visibleFilterCategories,
  filtersSearched,
  filters,
  filtersName,
  constants,
  company,
  showFilter,
}: Props) {
  const { t } = useTranslation();

  // Filters outside the "most used" category require the all-filters package.
  const isFilterLocked = (filter: TableFilter): boolean =>
    !company?.package.can_use_all_filters && filter.category !== 'most_used';

  // Select values resolve through the shared labeller (constants → filtersName →
  // raw) so they read correctly on initial load; other types format via filterToText.
  // Date ranges are stored under _start/_end, so build the [start, end] pair
  // filterToText expects (undefined when neither bound is set, so inactive date
  // filters show no summary).
  const filterValueSummary = (filter: TableFilter) => {
    if (filter.filter_type === 'select') {
      return labelFilterValue(filter, filters, constants, filtersName);
    }
    const rangeStart = filters[`${filter.name}${RANGE_START_SUFFIX}`];
    const rangeEnd = filters[`${filter.name}${RANGE_END_SUFFIX}`];
    const value =
      filter.filter_type === 'date'
        ? rangeStart || rangeEnd
          ? [rangeStart, rangeEnd]
          : undefined
        : filters[filter.name];
    return filterToText(
      filter.filter_type,
      value,
      filters[`${filter.name}${HANDLER_SUFFIX}`],
      filters[`${filter.name}${OPERATOR_SUFFIX}`],
      filters[`${MIN_PREFIX}${filter.name}`],
      filters[`${MAX_PREFIX}${filter.name}`],
    );
  };

  const renderFilterRow = (filter: TableFilter) => {
    const isActive = isFilterActive(filter, filters);
    const isLocked = isFilterLocked(filter);
    const summary = filterValueSummary(filter);

    return (
      <NavLink
        key={filter.id}
        label={filter.name_translated}
        description={summary || undefined}
        active={isActive}
        color="purple"
        onClick={showFilter(filter)}
        title={isLocked ? t(i18nKeys.NOT_INCLUDED) : undefined}
        rightSection={<Icon name={IconName.MINIMAL_RIGHT} />}
        style={{
          borderRadius: 'var(--mantine-radius-sm)',
          opacity: isLocked ? 0.6 : undefined,
        }}
      />
    );
  };

  const activeFilters = filtersSearched.filter(filter => isFilterActive(filter, filters));
  const categoriesInOrder = Object.keys(visibleFilterCategories).sort(
    (a, b) => visibleFilterCategories[a] - visibleFilterCategories[b],
  );

  return (
    <Stack gap="lg">
      <TextInput
        value={searchText}
        onChange={event => setSearchInput(event.currentTarget.value)}
        label={t(i18nKeys.FORM.RESEARCH)}
      />
      {activeFilters.length > 0 && <Box>{activeFilters.map(renderFilterRow)}</Box>}
      {categoriesInOrder.map(category => (
        <Box key={category}>
          <Text size="sm" fw={700} c="purple.6" tt="uppercase" mb={4}>
            {t(`VIEWS.FILTERS.CATEGORY.${category.toUpperCase()}`)}
          </Text>
          {filtersSearched
            .filter(filter => filter.category === category && !isFilterActive(filter, filters))
            .map(renderFilterRow)}
        </Box>
      ))}
    </Stack>
  );
}
